import React from "react";
import { PauseCircle } from "lucide-react";
import SidebarNavIcon from "../ui/SidebarNavIcon";
import {
  FIXTURE_BUCKET_NAV,
  FIXTURE_TAB,
  fixtureTabLabel,
} from "../../utils/fixturesDeskHelpers";
import { FIXTURE_TAB_ICONS } from "../../utils/sidebarNavIcons";

const tabIcon = (tone) => FIXTURE_TAB_ICONS[tone] || FIXTURE_TAB_ICONS.upcoming;

const FixtureDeskSidebar = ({
  activeTab,
  onTabChange,
  tabCounts,
  suspendedCount,
  suspendedOnly,
  onSuspendedOnlyChange,
  layout = "toolbar",
}) => {
  const showSuspendedFilter = activeTab !== FIXTURE_TAB.PAST;

  const handleTabChange = (id) => {
    onTabChange(id);
    if (id === FIXTURE_TAB.PAST) onSuspendedOnlyChange(false);
  };

  if (layout === "rail") {
    return (
      <nav className="fd-board-lane-nav" role="tablist" aria-label="Fixture lanes">
        {FIXTURE_BUCKET_NAV.map(({ id, hint, tone }) => {
          const active = activeTab === id;
          const count = tabCounts[id] ?? 0;
          const Icon = tabIcon(tone);
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              title={hint}
              className={[
                "fd-board-lane",
                `fd-board-lane-${tone}`,
                active ? "fd-board-lane-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleTabChange(id)}
            >
              <span className="fd-board-lane-icon" aria-hidden>
                <SidebarNavIcon icon={Icon} size={11} />
              </span>
              <span className="fd-board-lane-copy">
                <strong>{fixtureTabLabel(id)}</strong>
                <em>{hint}</em>
              </span>
              <em className="fd-board-lane-count tabular-nums">{count}</em>
            </button>
          );
        })}

        {showSuspendedFilter ? (
          <button
            type="button"
            className={[
              "fd-board-lane",
              "fd-board-lane-paused",
              suspendedOnly ? "fd-board-lane-active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={suspendedOnly}
            title="Suspended only — paused on this lane"
            onClick={() => onSuspendedOnlyChange(!suspendedOnly)}
          >
            <span className="fd-board-lane-icon" aria-hidden>
              <PauseCircle size={11} />
            </span>
            <span className="fd-board-lane-copy">
              <strong>Paused only</strong>
              <em>Filter to suspended fixtures</em>
            </span>
            <em className="fd-board-lane-count tabular-nums">{suspendedCount}</em>
          </button>
        ) : null}
      </nav>
    );
  }

  return (
    <div className="fd-lane-toolbar fd-board-lanes-toolbar" aria-label="Fixture board filters">
      <div className="fd-lane-scroll-wrap">
        <nav className="fd-lane-scroll" role="tablist" aria-label="Fixture buckets">
          {FIXTURE_BUCKET_NAV.map(({ id, hint, tone }) => {
            const active = activeTab === id;
            const count = tabCounts[id] ?? 0;
            const Icon = tabIcon(tone);
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                title={hint}
                className={[
                  "fd-lane-chip",
                  `fd-lane-chip-${tone}`,
                  active ? "fd-lane-chip-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleTabChange(id)}
              >
                <span className="fd-lane-chip-icon" aria-hidden>
                  <SidebarNavIcon icon={Icon} size={12} />
                </span>
                <span className="fd-lane-chip-label">{fixtureTabLabel(id)}</span>
                <em className="fd-lane-chip-count tabular-nums">{count}</em>
              </button>
            );
          })}

          {showSuspendedFilter ? (
            <>
              <span className="fd-lane-scroll-divider" aria-hidden />
              <button
                type="button"
                className={[
                  "fd-lane-chip",
                  "fd-lane-chip-suspended",
                  suspendedOnly ? "fd-lane-chip-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={suspendedOnly}
                title="Suspended only — paused on this lane"
                onClick={() => onSuspendedOnlyChange(!suspendedOnly)}
              >
                <span className="fd-lane-chip-icon" aria-hidden>
                  <PauseCircle size={12} />
                </span>
                <span className="fd-lane-chip-label">Paused</span>
                <em className="fd-lane-chip-count tabular-nums">{suspendedCount}</em>
              </button>
            </>
          ) : null}
        </nav>
      </div>
    </div>
  );
};

export default FixtureDeskSidebar;
